import React, { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LongRentalServicePicker from '../components/longRent/LongRentalServicePicker';
import PublicInboxLink from '../components/longRent/PublicInboxLink';
import PurchaseTermsModal from '../components/purchase/PurchaseTermsModal';
import PurchaseRecoveryCard from '../components/purchase/PurchaseRecoveryCard';
import { CountryMark, ServiceMark, SmsIcon } from '../components/sms/SmsMarks';
import {
  ApiError,
  createClientOperationKey,
  createLongRentalQuote,
  fetchLongRentalCatalog,
  fetchLongRentalOrder,
  getApiErrorMessage,
  LongRentalCatalogSelection,
  purchaseLongRental,
  reconcileLongRentalOrder,
} from '../services/smsApi';
import {
  LongRentalCapability,
  LongRentalCatalog,
  LongRentalNumber,
  LongRentalPurchaseOrder,
  LongRentalQuote,
  LongRentalScope,
} from '../types/models';
import { formatUsd } from '../utils/format';
import {
  getCodeFromApiError,
  getOrderFromApiError,
  PurchaseRecoveryRecord,
  readPurchaseRecovery,
  savePurchaseRecovery,
} from '../utils/purchaseRecovery';

const RECOVERY_STORAGE_KEY = 'sms:long-rent:pending-order:v1';

function periodLabel(days: number): string {
  if (days === 365) return '1 год';
  return `${days} дней`;
}

function locationLabel(stateName: string | null, areaCode: string | null): string {
  if (areaCode) return `${stateName || 'США'} · ${areaCode}`;
  if (stateName) return `${stateName} · любой код штата`;
  return 'Любой штат и код';
}

const LongTermRental: React.FC = () => {
  const [catalog, setCatalog] = useState<LongRentalCatalog | null>(null);
  // Справочники независимы от цен: сохраняем их отдельно для каждого режима.
  const [selectorCatalog, setSelectorCatalog] = useState<LongRentalCatalog | null>(null);
  const modeCatalogs = useRef<Record<string, LongRentalCatalog>>({});
  const [rentalScope, setRentalScope] = useState<LongRentalScope>('all_services');
  const [rentalCapability, setRentalCapability] = useState<LongRentalCapability>('sms');
  const [serviceInput, setServiceInput] = useState('');
  const [serviceCode, setServiceCode] = useState<string | null>(null);
  const [stateName, setStateName] = useState<string | null>(null);
  const [areaCode, setAreaCode] = useState<string | null>(null);
  const [selectedDays, setSelectedDays] = useState<number | null>(null);
  const [quote, setQuote] = useState<LongRentalQuote | null>(null);
  const [purchaseOperationKey, setPurchaseOperationKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasedRental, setPurchasedRental] = useState<LongRentalNumber | null>(null);
  const [recoveryRecord, setRecoveryRecord] = useState<PurchaseRecoveryRecord | null>(() => readPurchaseRecovery(RECOVERY_STORAGE_KEY));
  const [recoveryOrder, setRecoveryOrder] = useState<LongRentalPurchaseOrder | null>(null);
  const [checkingRecovery, setCheckingRecovery] = useState(false);
  const [error, setError] = useState('');
  const periodRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const purchaseResultRef = useRef<HTMLElement | null>(null);
  const requestVersion = useRef(0);

  const currentSelection = (
    scope = rentalScope,
    nextServiceCode = serviceCode,
    nextStateName = stateName,
    nextAreaCode = areaCode,
  ): LongRentalCatalogSelection => ({
    rentalScope: scope,
    rentalCapability,
    serviceCode: scope === 'single_service' ? nextServiceCode : null,
    stateName: nextStateName,
    areaCode: nextAreaCode,
  });

  const clearCheckout = () => {
    setQuote(null);
    setPurchaseOperationKey(null);
  };

  const loadCatalog = async (selection: LongRentalCatalogSelection, initial = false) => {
    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setCatalog(null);
    const modeKey = `${selection.rentalScope}:${selection.rentalCapability || 'sms'}`;
    setSelectorCatalog(modeCatalogs.current[modeKey] || null);
    setQuoting(false);
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError('');
    clearCheckout();
    try {
      const next = await fetchLongRentalCatalog(selection);
      if (requestVersion.current !== version) return;
      setCatalog(next);
      modeCatalogs.current[modeKey] = next;
      setSelectorCatalog(next);
      setSelectedDays((current) => (
        next.offers.some((offer) => offer.days === current && offer.available)
          ? current
          : (next.offers.find((offer) => offer.available)?.days ?? null)
      ));
    } catch (loadError) {
      if (requestVersion.current === version) setError(getApiErrorMessage(loadError));
    } finally {
      if (requestVersion.current === version) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    void loadCatalog({ rentalScope: 'all_services', rentalCapability: 'sms' }, true);
    // Начальная загрузка выполняется один раз; дальнейшие запросы запускают контролы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (purchasedRental) purchaseResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [purchasedRental]);

  useEffect(() => {
    if (!recoveryRecord?.orderId) return undefined;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      try {
        const order = await fetchLongRentalOrder(recoveryRecord.orderId!);
        if (stopped) return;
        setRecoveryOrder(order);
        if (order.rental) {
          setPurchasedRental(order.rental);
          savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
          setRecoveryRecord(null);
          return;
        }
        if (order.terminal) return;
      } catch (pollError) {
        if (stopped) return;
        if (pollError instanceof ApiError && pollError.status === 404) {
          savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
          setRecoveryRecord(null);
          return;
        }
      }
      if (!stopped) timer = window.setTimeout(poll, 10000);
    };
    void poll();
    return () => { stopped = true; window.clearTimeout(timer); };
  }, [recoveryRecord?.orderId]);

  const selectedOffer = useMemo(
    () => catalog?.offers.find((offer) => offer.days === selectedDays) || null,
    [catalog, selectedDays],
  );
  const selectedService = useMemo(
    () => selectorCatalog?.services.find((service) => service.code === serviceCode) || null,
    [selectorCatalog, serviceCode],
  );
  const selectedLocation = useMemo(
    () => selectorCatalog?.locations.find((location) => location.stateName === stateName) || null,
    [selectorCatalog, stateName],
  );

  const selectPeriod = (days: number, focus = false) => {
    setSelectedDays(days);
    clearCheckout();
    if (focus) window.requestAnimationFrame(() => periodRefs.current[days]?.focus());
  };

  const handlePeriodKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentDays: number) => {
    if (!catalog || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const available = catalog.offers.filter((offer) => offer.available);
    if (!available.length) return;
    const currentIndex = Math.max(0, available.findIndex((offer) => offer.days === currentDays));
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? available.length - 1
        : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + available.length) % available.length;
    event.preventDefault();
    selectPeriod(available[nextIndex].days, true);
  };

  const changeMode = (scope: LongRentalScope, capability: LongRentalCapability) => {
    if (scope === rentalScope && capability === rentalCapability) return;
    setRentalScope(scope);
    setRentalCapability(capability);
    setServiceCode(null);
    setServiceInput('');
    setSelectedDays(null);
    void loadCatalog({
      ...currentSelection(scope, null),
      rentalCapability: capability,
    });
  };

  const changeServiceInput = (value: string) => {
    setServiceInput(value);
    if (serviceCode) {
      setServiceCode(null);
      setSelectedDays(null);
      void loadCatalog(currentSelection('single_service', null));
    }
  };

  const selectService = (service: { code: string; name: string }) => {
    if (service.code === serviceCode) return;
    setServiceInput('');
    setServiceCode(service.code);
    setSelectedDays(null);
    void loadCatalog(currentSelection('single_service', service.code));
  };

  const changeState = (value: string) => {
    const nextState = value || null;
    if (nextState === stateName) return;
    setStateName(nextState);
    setAreaCode(null);
    void loadCatalog(currentSelection(rentalScope, serviceCode, nextState, null));
  };

  const changeAreaCode = (value: string) => {
    const nextAreaCode = value || null;
    if (nextAreaCode === areaCode) return;
    setAreaCode(nextAreaCode);
    void loadCatalog(currentSelection(rentalScope, serviceCode, stateName, nextAreaCode));
  };

  const preparePurchase = async () => {
    if (!selectedOffer?.available || loading || refreshing || quoting) return;
    const version = requestVersion.current;
    setQuoting(true);
    setError('');
    try {
      const nextQuote = await createLongRentalQuote(selectedOffer.days, currentSelection());
      if (requestVersion.current !== version) return;
      const nextOperationKey = createClientOperationKey('long-rent');
      // Блок оплаты появляется только после подготовки idempotency-ключа.
      setPurchaseOperationKey(nextOperationKey);
      setQuote(nextQuote);
    } catch (quoteError) {
      if (requestVersion.current === version) setError(getApiErrorMessage(quoteError));
    } finally {
      if (requestVersion.current === version) setQuoting(false);
    }
  };

  const confirmPurchase = async () => {
    if (!quote || !purchaseOperationKey || !catalog?.purchasesEnabled || catalog.purchaseReadiness?.ready === false) return;
    setPurchasing(true);
    setError('');
    const pendingRecord: PurchaseRecoveryRecord = {
      quoteId: quote.id,
      operationKey: purchaseOperationKey,
      orderId: null,
      amount: quote.finalAmount,
      createdAt: new Date().toISOString(),
    };
    savePurchaseRecovery(RECOVERY_STORAGE_KEY, pendingRecord);
    setRecoveryRecord(pendingRecord);
    try {
      const result = await purchaseLongRental(quote.id, purchaseOperationKey);
      if (result.rental) {
        setPurchasedRental(result.rental);
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
        setRecoveryRecord(null);
        setRecoveryOrder(null);
        clearCheckout();
      }
      else {
        const nextRecord = { ...pendingRecord, orderId: result.id };
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
        setRecoveryOrder(result);
        setError('Сумма зарезервирована, выдача номера уточняется. Списание появится только после подтверждённой покупки.');
      }
    } catch (purchaseError) {
      const order = getOrderFromApiError(purchaseError) as LongRentalPurchaseOrder | null;
      if (order) {
        const nextRecord = { ...pendingRecord, orderId: order.id };
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
        setRecoveryOrder(order);
      } else if (getCodeFromApiError(purchaseError)) {
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
        setRecoveryRecord(null);
        setRecoveryOrder(null);
      }
      setError(getApiErrorMessage(purchaseError));
    } finally {
      setPurchasing(false);
    }
  };

  const checkRecovery = async () => {
    if (!recoveryRecord) return;
    setCheckingRecovery(true);
    setError('');
    try {
      const order = recoveryRecord.orderId
        ? await reconcileLongRentalOrder(recoveryRecord.orderId)
        : await purchaseLongRental(recoveryRecord.quoteId, recoveryRecord.operationKey);
      const nextRecord = { ...recoveryRecord, orderId: order.id };
      setRecoveryOrder(order);
      if (order.rental) {
        setPurchasedRental(order.rental);
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
        setRecoveryRecord(null);
        clearCheckout();
      } else {
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
      }
    } catch (recoveryError) {
      const order = getOrderFromApiError(recoveryError) as LongRentalPurchaseOrder | null;
      if (order) {
        const nextRecord = { ...recoveryRecord, orderId: order.id };
        savePurchaseRecovery(RECOVERY_STORAGE_KEY, nextRecord);
        setRecoveryRecord(nextRecord);
        setRecoveryOrder(order);
      }
      setError(getApiErrorMessage(recoveryError));
    } finally {
      setCheckingRecovery(false);
    }
  };

  const selectionSummary = rentalScope === 'single_service'
    ? `${rentalCapability === 'sms_voice' ? 'SMS + Voice · ' : ''}${selectedService?.name || 'Выберите сервис'}`
    : 'Любые сервисы';

  return (
    <div className="sms-pageStack sms-longRent">
      <header className="sms-pageIntro sms-longRent__intro">
        <div>
          <h1>Долгосрочная аренда</h1>
          <p>Номер США на срок от 7 дней до года. Доступны SMS-аренда и ограниченные предложения с входящими звонками.</p>
        </div>
        <div className="sms-longRent__geo" aria-label="Выбранное гео">
          <CountryMark name="США" />
          <span><small>Гео</small><strong>США</strong></span>
        </div>
      </header>

      {error && !selectedOffer ? <div className="sms-inlineAlert is-error" role="alert">{error}</div> : null}

      {recoveryRecord ? (
        <PurchaseRecoveryCard
          kindLabel="долгосрочная аренда"
          record={recoveryRecord}
          order={recoveryOrder}
          checking={checkingRecovery}
          onCheck={() => void checkRecovery()}
          onDismiss={() => {
            savePurchaseRecovery(RECOVERY_STORAGE_KEY, null);
            setRecoveryRecord(null);
            setRecoveryOrder(null);
          }}
        />
      ) : null}

      <section className="sms-card sms-longRent__picker" aria-labelledby="long-rent-type-title">
        <div className="sms-sectionHead">
          <h2 id="long-rent-type-title">Настройте номер</h2>
          <p>Выберите назначение номера. Voice доступен только для отмеченных сервисов; штат или код можно оставить любым.</p>
        </div>

        <div className="sms-longRent__modes" role="radiogroup" aria-label="Тип аренды">
          <button type="button" role="radio" aria-checked={rentalScope === 'all_services' && rentalCapability === 'sms'} className={rentalScope === 'all_services' && rentalCapability === 'sms' ? 'is-selected' : ''} onClick={() => changeMode('all_services', 'sms')}>
            <span><strong>Любые сервисы</strong><small>Один номер без привязки к сайту</small></span>
          </button>
          <button type="button" role="radio" aria-checked={rentalScope === 'single_service' && rentalCapability === 'sms'} className={rentalScope === 'single_service' && rentalCapability === 'sms' ? 'is-selected' : ''} onClick={() => changeMode('single_service', 'sms')}>
            <span><strong>Один сервис</strong><small>Дешевле, но только для выбранного сайта</small></span>
          </button>
          <button type="button" role="radio" aria-checked={rentalCapability === 'sms_voice'} className={rentalCapability === 'sms_voice' ? 'is-selected' : ''} onClick={() => changeMode('single_service', 'sms_voice')}>
            <SmsIcon name="headphones" />
            <span><strong>SMS + Voice</strong><small>SMS и входящие звонки для ограниченного списка</small></span>
          </button>
        </div>

        {rentalScope === 'single_service' && !selectorCatalog && (loading || refreshing) ? (
          <div className="sms-longRent__loading" role="status" aria-label="Загрузка сервисов"><span className="sms-longRent__spinner" aria-hidden="true" />Загружаем сервисы для этого режима…</div>
        ) : rentalScope === 'single_service' && selectorCatalog ? (
          <LongRentalServicePicker
            services={selectorCatalog.services}
            selectedCode={serviceCode}
            query={serviceInput}
            voiceOnly={rentalCapability === 'sms_voice'}
            onQueryChange={changeServiceInput}
            onSelect={selectService}
          />
        ) : null}

        <section className="sms-longRent__location" aria-labelledby="long-rent-location-title">
          <div className="sms-longRent__subhead">
            <div>
              <h3 id="long-rent-location-title">Выберите гео</h3>
              <p>Штат и код необязательны. Выбор штата может увеличить цену номера.</p>
            </div>
          </div>
          <div className="sms-longRent__countryRow">
            <CountryMark name="США" />
            <span><small>Страна</small><strong>США</strong></span>
            <span className="sms-longRent__countryOnly">Единственное доступное гео</span>
          </div>
          <div className="sms-longRent__filters">
            <label className="sms-field">
              <span>Штат</span>
              <select className="u2-input" value={stateName || ''} disabled={!selectorCatalog} onChange={(event) => changeState(event.target.value)}>
                <option value="">Любой штат</option>
                {!selectorCatalog && stateName ? <option value={stateName}>{stateName}</option> : null}
                {(selectorCatalog?.locations || []).map((location) => <option key={location.stateName} value={location.stateName}>{location.stateName}</option>)}
              </select>
            </label>

            <label className="sms-field">
              <span>Код номера</span>
              <select className="u2-input" value={areaCode || ''} disabled={!stateName || !selectorCatalog} onChange={(event) => changeAreaCode(event.target.value)}>
                <option value="">{stateName ? 'Любой код штата' : 'Сначала выберите штат'}</option>
                {!selectorCatalog && areaCode ? <option value={areaCode}>{areaCode}</option> : null}
                {(selectedLocation?.areaCodes || []).map((code) => <option key={code} value={code}>{code}</option>)}
              </select>
            </label>
          </div>
          {!selectorCatalog && (loading || refreshing) ? <small className="u2-muted" role="status">Загружаем штаты и коды…</small> : null}
        </section>

        <div className="sms-longRent__selectionLine" aria-live="polite">
          <span>{selectionSummary}</span>
          <span>{locationLabel(stateName, areaCode)}</span>
        </div>

        {catalog?.purchaseReadiness?.ready === false ? (
          <div className="sms-inlineAlert is-info" role="status">{catalog.purchaseReadiness.message}</div>
        ) : null}

        <div className="sms-sectionHead sms-longRent__periodHead">
          <h2 id="long-rent-period-title">Выберите срок</h2>
          <p>Доступные тарифы подсвечены. Выбранный код номера подтверждается при выдаче. Цена фиксируется на 5 минут перед оплатой.</p>
        </div>

        {loading || refreshing ? (
          <div className="sms-longRent__priceLoading" role="status" aria-label="Обновление цен">
            <span className="sms-longRent__spinner" aria-hidden="true" />
            <span><strong>Обновляем цены и наличие</strong><small>Для выбранного сервиса, штата и кода номера.</small></span>
          </div>
        ) : null}

        {!loading && !refreshing && catalog ? (
          <div className={`sms-longRent__periods ${refreshing ? 'is-refreshing' : ''}`} role="radiogroup" aria-labelledby="long-rent-period-title">
            {catalog.offers.map((offer) => (
              <button
                key={offer.days}
                type="button"
                role="radio"
                ref={(node) => { periodRefs.current[offer.days] = node; }}
                className={`sms-longRent__period ${offer.available ? 'is-available' : 'is-unavailable'} ${selectedDays === offer.days ? 'is-selected' : ''}`}
                disabled={!offer.available || refreshing}
                onClick={() => selectPeriod(offer.days)}
                onKeyDown={(event) => handlePeriodKeyDown(event, offer.days)}
                aria-checked={selectedDays === offer.days}
                tabIndex={offer.available && selectedDays === offer.days ? 0 : -1}
              >
                <span>{periodLabel(offer.days)}</span>
                <strong>{offer.price !== undefined ? formatUsd(offer.price) : 'Недоступно'}</strong>
                <small>{selectedDays === offer.days ? 'Выбрано · ' : ''}{offer.available ? (offer.renewable ? 'можно продлить' : 'конечный срок') : offer.reason}</small>
              </button>
            ))}
          </div>
        ) : null}

        {!loading && !refreshing && catalog && !catalog.offers.some((offer) => offer.available) ? (
          <div className="sms-longRent__empty">
            <span><strong>{rentalScope === 'single_service' && !serviceCode ? 'Выберите сервис из списка' : 'Сейчас нет доступных предложений'}</strong><small>{rentalScope === 'single_service' && !serviceCode ? 'После выбора появятся цены для всех сроков.' : 'Измените штат, код или повторите запрос позже.'}</small></span>
            <button type="button" className="u2-btn is-secondary is-sm" onClick={() => void loadCatalog(currentSelection())}>Проверить снова</button>
          </div>
        ) : null}

        {!loading && !refreshing && selectedOffer?.available && !quote ? (
          <div className="sms-longRent__checkoutBar">
            <span><small>{selectionSummary} · {locationLabel(stateName, areaCode)} · {periodLabel(selectedOffer.days)}</small><strong>{formatUsd(selectedOffer.price || 0)}</strong></span>
            <button type="button" data-purchase-trigger="long-rent" className="u2-btn is-primary" onClick={() => void preparePurchase()} disabled={quoting || refreshing}>
              {quoting ? 'Фиксируем цену…' : 'Купить'}
            </button>
          </div>
        ) : null}
        {error && selectedOffer ? <div className="sms-inlineAlert is-error" role="alert">{error}</div> : null}
        {!loading && !refreshing && !catalog && error ? (
          <button type="button" className="u2-btn is-secondary" onClick={() => void loadCatalog(currentSelection())}>Повторить загрузку предложений</button>
        ) : null}
      </section>

      {quote ? (
        <PurchaseTermsModal
          title="Условия аренды"
          summary={`${quote.service?.name || 'Любые сервисы'} · ${locationLabel(quote.location.stateName, quote.location.areaCode)} · ${periodLabel(quote.durationDays)}`}
          price={formatUsd(quote.finalAmount)}
          returnFocusSelector="[data-purchase-trigger='long-rent']"
          purchaseDisabled={!catalog?.purchasesEnabled || catalog.purchaseReadiness?.ready === false}
          purchasePending={purchasing}
          onPurchase={() => void confirmPurchase()}
          onCancel={clearCheckout}
        >
          {selectedOffer?.price !== undefined && Math.round(selectedOffer.price * 100) !== Math.round(quote.finalAmount * 100) ? (
            <div className="sms-inlineAlert is-info" role="status">
              Цена обновилась: в каталоге {formatUsd(selectedOffer.price)}, перед оплатой {formatUsd(quote.finalAmount)}. Проверьте новую сумму перед подтверждением.
            </div>
          ) : null}
          <dl className="sms-longRent__terms">
            <div><dt>Аренда</dt><dd>{quote.terms.rental}</dd></div>
            <div><dt>Отмена</dt><dd>{quote.terms.cancellation}</dd></div>
            <div><dt>Продление</dt><dd>{quote.terms.renewal}</dd></div>
            <div><dt>Возврат</dt><dd>{quote.terms.refund}</dd></div>
          </dl>
          {!catalog?.purchasesEnabled ? (
            <div className="sms-inlineAlert is-info" role="status">Условия и актуальные цены доступны для проверки, но оплата пока выключена администратором.</div>
          ) : catalog.purchaseReadiness?.ready === false ? (
            <div className="sms-inlineAlert is-info" role="status">{catalog.purchaseReadiness.message}</div>
          ) : null}
        </PurchaseTermsModal>
      ) : null}

      {purchasedRental ? (
        <section ref={purchaseResultRef} className="sms-card sms-longRent__purchased" aria-labelledby="long-rent-purchased-title">
          <div className="sms-longRent__purchasedHead">
            <span className="sms-longRent__successMark"><SmsIcon name="check" /></span>
            <div>
              <h2 id="long-rent-purchased-title">Номер добавлен</h2>
              <p>Аренда уже доступна здесь и в разделе «Мои номера».</p>
            </div>
            <Link to="/active-numbers" className="u2-btn is-primary">Перейти в Мои номера</Link>
          </div>
          <div className="sms-tableWrap">
            <table className="sms-table sms-longRent__resultTable">
              <thead><tr><th>Номер</th><th>Тип</th><th>Сервис</th><th>Гео</th><th>Срок</th><th>Статус</th></tr></thead>
              <tbody><tr>
                <td data-label="Номер"><strong>{purchasedRental.phoneNumber}</strong></td>
                <td data-label="Тип">{purchasedRental.rentalCapability === 'sms_voice' ? 'SMS + Voice' : 'SMS'}</td>
                <td data-label="Сервис"><span className="sms-entityPair"><ServiceMark code={purchasedRental.service?.code || 'all'} name={purchasedRental.service?.name || 'Любые сервисы'} /><span>{purchasedRental.service?.name || 'Любые сервисы'}</span></span></td>
                <td data-label="Гео"><span className="sms-entityPair"><CountryMark name="США" /><span>{purchasedRental.location.stateName || 'США'}{purchasedRental.location.areaCode ? ` · ${purchasedRental.location.areaCode}` : ''}</span></span></td>
                <td data-label="Срок">{periodLabel(purchasedRental.durationDays)}</td>
                <td data-label="Статус"><span className={`sms-longNumber__state is-${purchasedRental.state}`}>{purchasedRental.state === 'active' ? 'Активен' : 'Статус уточняется'}</span></td>
              </tr></tbody>
            </table>
          </div>
          <PublicInboxLink path={purchasedRental.publicInboxPath} />
        </section>
      ) : null}
    </div>
  );
};

export default LongTermRental;
